(() => {
  const DESKTOP_QUERY = '(min-width: 750px)';
  const FOCUSABLE_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const desktopMedia = window.matchMedia(DESKTOP_QUERY);

  function getHeader() {
    return document.getElementById('header-component');
  }

  function getKeyFromTrigger(trigger) {
    return trigger?.getAttribute('data-gvs-mega-trigger') || '';
  }

  function getPanelForKey(key) {
    if (!key) return null;

    return document.querySelector(
      `[data-gvs-mega-panel="${CSS.escape(key)}"]:not(.c-header-collection-menu--mobile)`
    );
  }

  function getFocusable(panel) {
    if (!panel) return [];

    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      return !element.hidden && !element.hasAttribute('inert') && element.getClientRects().length > 0;
    });
  }

  function releaseScrollLockIfSafe() {
    if (!document.querySelector('[scroll-lock][open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  function initMegaMenus() {
    window.__gvsMegaMenuAbortController?.abort();

    const header = getHeader();
    if (!header) return;

    const controller = new AbortController();
    const { signal } = controller;
    window.__gvsMegaMenuAbortController = controller;

    const triggers = Array.from(header.querySelectorAll('[data-gvs-mega-trigger]'));
    const panels = Array.from(
      document.querySelectorAll('[data-gvs-mega-panel]:not(.c-header-collection-menu--mobile)')
    );

    if (!triggers.length || !panels.length) return;

    header.dataset.gvsMegaMenusReady = 'true';

    let closeTimer = 0;
    let activeKey = '';
    let activeTrigger = null;
    let keyboardInteraction = false;
    let headerBottomFrame = 0;

    const isDesktop = () => desktopMedia.matches;

    function scheduleHeaderBottom() {
      window.cancelAnimationFrame(headerBottomFrame);
      headerBottomFrame = window.requestAnimationFrame(() => {
        const rect = header.getBoundingClientRect();
        const bottom = Math.max(0, Math.round(rect.bottom));
        document.documentElement.style.setProperty('--gvs-header-bottom', `${bottom}px`);
      });
    }

    function closeNativeDropdowns() {
      header
        .querySelectorAll('.menu-list__link[aria-expanded="true"]:not([data-gvs-mega-trigger])')
        .forEach((link) => link.setAttribute('aria-expanded', 'false'));
    }

    function setTriggerState(key = '') {
      triggers.forEach((trigger) => {
        const isActive = getKeyFromTrigger(trigger) === key;
        trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        trigger.classList.toggle('is-active', isActive);
      });
    }

    function setPanelState(key = '') {
      panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-gvs-mega-panel') === key;
        panel.classList.toggle('is-open', isActive);
        panel.classList.toggle('active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        panel.toggleAttribute('inert', !isActive);
      });
    }

    function openMenu(trigger, { focusFirst = false } = {}) {
      if (!isDesktop()) return;

      const key = getKeyFromTrigger(trigger);
      const panel = getPanelForKey(key);
      if (!key || !panel) return;

      window.clearTimeout(closeTimer);
      closeNativeDropdowns();
      scheduleHeaderBottom();

      activeKey = key;
      activeTrigger = trigger;
      setTriggerState(key);
      setPanelState(key);
      document.documentElement.classList.add('gvs-header-menu-is-open');

      if (focusFirst) {
        window.requestAnimationFrame(() => getFocusable(panel)[0]?.focus({ preventScroll: true }));
      }
    }

    function closeMenus({ restoreFocus = false } = {}) {
      window.clearTimeout(closeTimer);
      const triggerToRestore = activeTrigger;

      activeKey = '';
      activeTrigger = null;
      setTriggerState();
      setPanelState();
      document.documentElement.classList.remove('gvs-header-menu-is-open');

      if (restoreFocus && triggerToRestore instanceof HTMLElement && triggerToRestore.isConnected) {
        window.requestAnimationFrame(() => triggerToRestore.focus({ preventScroll: true }));
      }
    }

    function queueClose(delay = 130) {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => closeMenus(), delay);
    }

    function resetDesktopHeaderState() {
      if (!isDesktop()) return;

      const headerMenu =
        header.querySelector('[ref="headerMenu"]') || header.querySelector('.header-menu');
      const drawerContainer =
        header.querySelector('[ref="headerDrawerContainer"]') ||
        header.querySelector('.header-drawer') ||
        header.querySelector('[data-header-drawer]');

      if (headerMenu instanceof HTMLElement) {
        headerMenu.classList.remove('hidden');
        headerMenu.removeAttribute('hidden');
        headerMenu.style.removeProperty('display');
      }

      if (drawerContainer instanceof HTMLElement) {
        drawerContainer.classList.add('desktop:hidden');
        drawerContainer.removeAttribute('open');
        drawerContainer.removeAttribute('data-open');

        drawerContainer.querySelectorAll('details').forEach((details) => {
          details.classList.remove('menu-open');
          details.removeAttribute('open');
          details
            .querySelector(':scope > summary')
            ?.setAttribute('aria-expanded', 'false');
        });
      }

      closeMenus();
      releaseScrollLockIfSafe();
    }

    triggers.forEach((trigger) => {
      trigger.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'mouse') openMenu(trigger);
        },
        { signal }
      );

      trigger.addEventListener(
        'focus',
        () => {
          if (keyboardInteraction) openMenu(trigger);
        },
        { signal }
      );

      trigger.addEventListener(
        'click',
        (event) => {
          if (!isDesktop()) return;

          event.preventDefault();
          event.stopPropagation();

          const key = getKeyFromTrigger(trigger);
          if (activeKey === key) {
            closeMenus({ restoreFocus: true });
          } else {
            openMenu(trigger);
          }
        },
        { signal }
      );

      trigger.addEventListener(
        'keydown',
        (event) => {
          if (!isDesktop()) return;

          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openMenu(trigger, { focusFirst: true });
          } else if (event.key === 'Escape') {
            event.preventDefault();
            closeMenus({ restoreFocus: true });
          }
        },
        { signal }
      );
    });

    panels.forEach((panel) => {
      panel.addEventListener('pointerenter', () => window.clearTimeout(closeTimer), { signal });
      panel.addEventListener('pointerleave', () => queueClose(), { signal });

      panel.addEventListener(
        'focusin',
        () => {
          window.clearTimeout(closeTimer);
          activeKey = panel.getAttribute('data-gvs-mega-panel') || '';
          activeTrigger =
            triggers.find((trigger) => getKeyFromTrigger(trigger) === activeKey) || activeTrigger;
          setTriggerState(activeKey);
          setPanelState(activeKey);
        },
        { signal }
      );

      panel.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget instanceof Node && panel.contains(event.relatedTarget)) return;
          queueClose();
        },
        { signal }
      );

      panel.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            closeMenus({ restoreFocus: true });
          }
        },
        { signal }
      );
    });

    header.querySelectorAll('.menu-list__list-item').forEach((item) => {
      if (item.querySelector('[data-gvs-mega-trigger]')) return;
      item.addEventListener('pointerenter', () => activeKey && closeMenus(), { signal });
      item.addEventListener('focusin', () => activeKey && closeMenus(), { signal });
    });

    header.addEventListener(
      'pointerleave',
      (event) => {
        const next = event.relatedTarget;
        const movedIntoPanel = next instanceof Node && panels.some((panel) => panel.contains(next));
        if (!movedIntoPanel) queueClose();
      },
      { signal }
    );

    document.addEventListener(
      'pointerdown',
      (event) => {
        keyboardInteraction = false;

        const target = event.target;
        const clickedTrigger = target instanceof Node && triggers.some((trigger) => trigger.contains(target));
        const clickedPanel = target instanceof Node && panels.some((panel) => panel.contains(target));
        if (!clickedTrigger && !clickedPanel) closeMenus();
      },
      { capture: true, signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Tab' || event.key.startsWith('Arrow')) {
          keyboardInteraction = true;
        }

        if (event.key === 'Escape' && activeKey) {
          event.preventDefault();
          closeMenus({ restoreFocus: true });
        }
      },
      { capture: true, signal }
    );

    desktopMedia.addEventListener(
      'change',
      (event) => {
        if (event.matches) {
          window.setTimeout(resetDesktopHeaderState, 50);
        } else {
          closeMenus();
        }
      },
      { signal }
    );

    window.addEventListener(
      'resize',
      () => {
        if (activeKey) scheduleHeaderBottom();
        if (!isDesktop()) closeMenus();
      },
      { passive: true, signal }
    );

    window.addEventListener(
      'orientationchange',
      () => {
        if (activeKey) window.setTimeout(scheduleHeaderBottom, 120);
      },
      { passive: true, signal }
    );

    window.addEventListener(
      'scroll',
      () => {
        if (activeKey) scheduleHeaderBottom();
      },
      { passive: true, signal }
    );

    window.addEventListener(
      'pagehide',
      () => closeMenus(),
      { passive: true, signal }
    );

    window.addEventListener(
      'pageshow',
      () => {
        setPanelState();
        if (activeKey) scheduleHeaderBottom();
        if (isDesktop()) resetDesktopHeaderState();
      },
      { passive: true, signal }
    );

    setPanelState();
    if (isDesktop()) resetDesktopHeaderState();
  }

  window.__gvsMegaMenuBootstrapController?.abort();
  const bootstrapController = new AbortController();
  const { signal: bootstrapSignal } = bootstrapController;
  window.__gvsMegaMenuBootstrapController = bootstrapController;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaMenus, {
      once: true,
      signal: bootstrapSignal,
    });
  } else {
    initMegaMenus();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.querySelector('#header-component')) return;
      window.setTimeout(initMegaMenus, 50);
    },
    { signal: bootstrapSignal }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.querySelector('#header-component')) return;

      window.__gvsMegaMenuAbortController?.abort();
      document.documentElement.classList.remove('gvs-header-menu-is-open');
    },
    { signal: bootstrapSignal }
  );
})();
